export interface SanitizeResult { html: string; chapterAnchors: string[]; }

// Strip style/script blocks (they pollute global CSS — e.g. `clear:both` on headings breaks
// CSS column layout, and `body { margin-left: 10% }` shifts the whole page) plus inline
// event handlers from the old CF JS runtime.
//
// Chapter anchors are detected generically: any anchor whose ID is referenced by an
// href="#..." link in the first 20% of the document (the TOC area) and also exists in the
// full document body. This handles chapter headings, image-index links, and any other
// named bookmark — without needing book-specific naming conventions.
//
// Legacy CF books (e.g. Walden) use JS-driven TOC navigation:
//   <a href="#one" onClick="gototocchapter(N);">Title</a>
// For these, the dummy href is rewritten to the real cN/WN anchor before onClick is stripped.
export function sanitizeLegacyHtml(html: string): SanitizeResult {
  // 1. Strip embedded CSS/JS and event handlers first so the TOC scan
  //    is not confused by script text that contains href="#" patterns.
  html = html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/\s+on\w+="[^"]*"/gi, '');

  // 2. gototocchapter rewriting for legacy CF books (Walden and similar).
  //    The real chapter anchors are id/name="cN" or "WN" in document order.
  if (html.includes('gototocchapter')) {
    const cfAnchors: string[] = [];
    const seenNums = new Set<number>();
    const cfRe = /(?:id|name)="([cW]\d+)"/gi;
    let m: RegExpExecArray | null;
    while ((m = cfRe.exec(html)) !== null) {
      const num = parseInt(m[1].replace(/\D+/g, ''), 10);
      if (!isNaN(num) && !seenNums.has(num)) { seenNums.add(num); cfAnchors.push(m[1]); }
    }
    if (cfAnchors.length > 0) {
      html = html.replace(/<a\s([^>]*)>/gi, (full, attrs: string) => {
        if (!attrs.includes('gototocchapter')) return full;
        const nm = /gototocchapter\((\d+)\)/.exec(attrs);
        if (!nm) return full;
        const idx = parseInt(nm[1], 10) - 1;
        if (idx < 0 || idx >= cfAnchors.length) return full;
        return `<a ${attrs.replace(/href="[^"]*"/, `href="#${cfAnchors[idx]}"`)}>`;
      });
    }
  }

  // 3. Inject placeholder style into every <img> so the initial CSS column layout is
  //    stable. Without this, each image that loads triggers a full column reflow of
  //    the entire document — catastrophic for long books with many images.
  //    When width/height attrs are present we preserve the aspect-ratio so the
  //    placeholder occupies the same space as the real image; otherwise a safe
  //    minimum height prevents images from collapsing to 0px.
  html = html.replace(/<img([^>]*)>/gi, (_m: string, attrs: string) => {
    if (/\bstyle\s*=/i.test(attrs)) return `<img${attrs}>`;
    const w = /\bwidth\s*=\s*['"]?(\d+)['"]?/i.exec(attrs)?.[1];
    const h = /\bheight\s*=\s*['"]?(\d+)['"]?/i.exec(attrs)?.[1];
    const placeholder = w && h
      ? `aspect-ratio:${w}/${h};width:min(100%,${w}px);object-fit:contain;background-color:#f0f0f0`
      : `min-height:160px;width:100%;background-color:#f0f0f0`;
    return `<img${attrs} style="${placeholder}">`;
  });

  // 4. Build a full-document anchor inventory: id/name value → byte offset.
  const allAnchors = new Map<string, number>();
  const anchorRe = /(?:id|name)="([^"]+)"/gi;
  let am: RegExpExecArray | null;
  while ((am = anchorRe.exec(html)) !== null) {
    if (!allAnchors.has(am[1])) allAnchors.set(am[1], am.index);
  }

  // 5. Scan the TOC area (first 20%, capped at 150 KB) for href="#..." links.
  const tocEnd = Math.min(Math.floor(html.length * 0.2), 150_000);
  const tocSlice = html.slice(0, tocEnd);
  const tocLinkRe = /href="#([^"]+)"/gi;
  const tocReferenced = new Set<string>();
  let tm: RegExpExecArray | null;
  while ((tm = tocLinkRe.exec(tocSlice)) !== null) {
    tocReferenced.add(decodeURIComponent(tm[1]));
  }

  // 6. Intersect: only anchors both referenced in the TOC and present in the body,
  //    sorted by their position in the document.
  const chapterAnchors = Array.from(tocReferenced)
    .filter(id => allAnchors.has(id))
    .sort((a, b) => allAnchors.get(a)! - allAnchors.get(b)!);

  return { html, chapterAnchors };
}
